<template>
  <div class="jarvis-knowledge-container">
    <!-- Notification Overlay -->
    <transition name="fade">
      <div v-if="notification.show" class="notification-overlay" :class="notification.type">
        <div class="notification-content">
          <div class="notification-icon">
            <svg v-if="notification.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <span class="notification-message">{{ notification.message }}</span>
        </div>
      </div>
    </transition>

    <div class="header-stats-container">
      <PageHeader 
        title="Base de Connaissances IA"
        subtitle="Configuration et entraînement de votre agent Jarvis"
        :breadcrumbs="[{ label: 'GRIOT IA', active: true }]"
        :show-back="true"
        @back="goBack"
      >
        <template #actions>
          <div class="header-actions">
            <!-- GRIOT IA Badge/Toggle -->
            <div class="griot-badge" :class="{ 'active': chatbot?.jarvis }">
              <div class="badge-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <span class="badge-text">{{ chatbot?.jarvis ? 'GRIOT IA ACTIVÉ' : 'GRIOT IA DÉSACTIVÉ' }}</span>
              <div class="badge-toggle" @click="toggleJarvis">
                <div class="toggle-dot"></div>
              </div>
            </div>

            <!-- Bot Avatar -->
            <div class="bot-avatar-context" v-if="chatbot">
              <img v-if="chatbotAvatarUrl" :src="chatbotAvatarUrl" :alt="chatbot?.name" class="bot-img" />
              <div v-else class="bot-initials">{{ chatbot?.name?.charAt(0) }}</div>
              <div class="status-indicator"></div>
            </div>
          </div>
        </template>
      </PageHeader>

      <div class="stats-bar">
        <!-- Files Count Card -->
        <div class="stats-card">
          <div class="stats-icon files">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <div class="stats-info">
            <span class="stats-label">Documents entraînés</span>
            <div class="stats-value">
              <span class="count">{{ knowledgeStats.totalFiles }}</span>
              <span class="unit">fichiers</span>
            </div>
          </div>
        </div>

        <!-- Scraping Progress Card -->
        <div class="stats-card">
          <div class="stats-icon scraping">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
          </div>
          <div class="stats-info">
            <span class="stats-label">Processus d'extraction</span>
            <div class="stats-value">
              <span class="count">{{ scrapingStats.scrapedBlocks }} / {{ scrapingStats.totalBlocks }}</span>
              <span class="unit">blocs</span>
            </div>
          </div>
        </div>

        <!-- Storage Card -->
        <div class="stats-card">
          <div class="stats-icon storage">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
              <line x1="12" y1="22.08" x2="12" y2="12"></line>
            </svg>
          </div>
          <div class="stats-info">
            <span class="stats-label">Espace utilisé</span>
            <div class="stats-value">
              <span class="count">{{ knowledgeStats.totalSize }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="knowledge-main">
      <!-- Scraping Section - Full Width -->
      <div class="scraping-section">
        <div class="scraping-card">
          <div class="card-header">
            <div class="card-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="3" y1="9" x2="21" y2="9"></line>
                <line x1="9" y1="21" x2="9" y2="9"></line>
              </svg>
            </div>
            <div class="card-title">
              <h3>Extraire les connaissances de vos flux</h3>
              <p>Analyser tous vos flux pour identifier les blocs à intégrer dans la base de connaissances</p>
            </div>
          </div>
          
          <div class="scraping-content">
            <!-- Scraping Stats -->
            <div class="scraping-stats-top" v-if="availableBlocks.length > 0">
              <div class="stats-grid">
                <div class="stat-card total">
                  <span class="stat-number">{{ availableBlocks.length }}</span>
                  <span class="stat-label">Blocs détectés</span>
                </div>
                <div class="stat-card active">
                  <span class="stat-number">{{ selectedBlocks.length }}</span>
                  <span class="stat-label">Blocs activés</span>
                </div>
              </div>
            </div>

            <!-- Scraping Controls -->
            <div class="scraping-controls">
              <div class="search-blocks">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="M21 21l-4.35-4.35"></path>
                </svg>
                <input 
                  type="text" 
                  placeholder="Rechercher des blocs..." 
                  v-model="blocksSearchQuery"
                  @input="filterBlocks"
                />
              </div>
              <div class="action-buttons">
                <button 
                  class="btn-trigger-scraping" 
                  @click="triggerScraping" 
                  :disabled="isScraping"
                >
                  <svg v-if="!isScraping" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                  </svg>
                  <svg v-else class="spinning" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 2a10 10 0 1 0 10 10H12V2z"/>
                  </svg>
                  <span>{{ isScraping ? 'Extraction en cours...' : 'Relancer l\'analyse' }}</span>
                </button>
              </div>
            </div>

            <!-- Blocks Grid -->
            <div class="blocks-grid" v-if="filteredBlocks.length > 0">
              <div 
                v-for="block in filteredBlocks" 
                :key="block._id" 
                class="block-card"
                :class="{ 'active': selectedBlocks.includes(block._id) }"
              >
                <div class="block-check">
                  <input 
                    type="checkbox" 
                    :id="'block-' + block._id" 
                    :value="block._id"
                    v-model="selectedBlocks"
                    @change="toggleBlockInKnowledge(block._id)"
                  />
                  <label :for="'block-' + block._id"></label>
                </div>
                <div class="block-info">
                  <div class="block-name">{{ block.name || 'Bloc sans nom' }}</div>
                  <div class="block-type">{{ block.type }}</div>
                  <div class="block-id">ID: {{ block._id }}</div>
                </div>
              </div>
            </div>
            
            <div v-if="isScraping" class="scraping-progress-overlay">
              <div class="progress-container">
                <div class="progress-spinner"></div>
                <div class="progress-details">
                  <h4>Analyse des connaissances...</h4>
                  <p>{{ scrapingProgress.current }} / {{ scrapingProgress.total }} blocs analysés</p>
                </div>
              </div>
            </div>

            <div v-else-if="filteredBlocks.length === 0" class="no-blocks">
              <p>Aucun bloc trouvé. Lancez une analyse pour commencer.</p>
            </div>
            
            <!-- AI Prompt Configuration Section -->
            <div class="ai-configuration-section">
              <div class="section-divider"></div>
              
              <div class="config-header">
                <div class="config-title">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 1 1-7.6-10.6 8.38 8.38 0 0 1 3.8.9L21 2.5l-2.5 5.5"></path>
                  </svg>
                  <h3>Instructions de l'IA (System Prompt)</h3>
                </div>
                <div class="config-actions">
                  <button class="btn-generate-prompt" @click="generateBasePrompt" :disabled="claudeIntegration.isProcessing">
                    <svg v-if="!claudeIntegration.isProcessing" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                    </svg>
                    <span v-else class="loader"></span>
                    <span>Synchroniser avec les blocs</span>
                  </button>
                  <button class="btn-save-prompt" @click="savePrompt" :disabled="!hasPromptChanged || isSavingPrompt">
                    {{ isSavingPrompt ? 'Sauvegarde...' : 'Sauvegarder' }}
                  </button>
                </div>
              </div>

              <div class="prompt-editor-container">
                <div class="editor-header">
                  <div class="prompt-stats">
                    <span class="stat-item">
                      <strong>{{ claudeIntegration.promptStats.activeBlocks }}</strong> blocs actifs
                    </span>
                    <span class="stat-item">
                      <strong>{{ editablePrompt.length }}</strong> caractères
                    </span>
                  </div>
                  <div class="editor-actions">
                    <button class="btn-toggle-view" @click="showPromptContent = !showPromptContent">
                      {{ showPromptContent ? 'Masquer' : 'Afficher' }} les instructions
                    </button>
                  </div>
                </div>

                <div v-show="showPromptContent" class="prompt-content-area">
                  <textarea 
                    v-model="editablePrompt" 
                    placeholder="Le prompt système sera généré ici à partir de vos blocs..."
                    class="prompt-textarea"
                  ></textarea>
                  <div class="prompt-helper-text">
                    <p>Ce texte définit la personnalité et les connaissances de votre agent. Il est automatiquement mis à jour lorsque vous effectuez une synchronisation, mais vous pouvez également le modifier manuellement.</p>
                  </div>
                </div>
              </div>

              <!-- AI Test Console -->
              <div class="ai-test-console" v-if="editablePrompt">
                <div class="console-header">
                  <h4>Console de Test IA</h4>
                  <div class="console-status" :class="{ 'testing': isTesting }">
                    {{ isTesting ? 'IA en train de réfléchir...' : 'Prêt pour le test' }}
                  </div>
                </div>

                <div class="console-body">
                  <div class="test-input-group">
                    <textarea 
                      v-model="testMessage" 
                      placeholder="Posez une question à votre IA pour tester ses connaissances..."
                      @keyup.ctrl.enter="runAiTest"
                    ></textarea>
                    <div class="console-actions">
                      <button class="btn-run-test" @click="runAiTest" :disabled="isTesting || !testMessage.trim()">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                        </svg>
                        <span>Tester l'IA</span>
                      </button>
                    </div>
                    <div class="console-hint">
                      <span>💡 Astuce: Utilisez Ctrl+Entrée pour envoyer rapidement</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Test Results -->
              <div v-if="testResults.length > 0" class="test-results">
                <h6>Résultats des tests :</h6>
                <div class="test-result-list">
                  <div v-for="(result, index) in testResults" :key="index" class="test-result-item">
                    <div class="test-message">
                      <strong>Message:</strong> {{ result.message }}
                    </div>
                    <div class="test-response" :class="result.success ? 'success' : 'error'">
                      <strong>Réponse:</strong>
                      <div v-if="result.success" class="response-success">
                        <!-- Informations principales -->
                        <div class="response-summary">
                          <div class="response-item">
                            <strong>Type:</strong> {{ result.response?.responseType || 'N/A' }}
                          </div>
                          <div class="response-item" v-if="result.response?.redirection_block">
                            <strong>Redirection:</strong> 
                            <span class="redirection-block">{{ result.response.redirection_block }}</span>
                          </div>
                          <div class="response-item" v-if="result.model">
                            <strong>Modèle:</strong> {{ result.model }}
                          </div>
                          <div class="response-item" v-if="result.usage">
                            <strong>Tokens utilisés:</strong> 
                            Input: {{ result.usage.input_tokens }}, 
                            Output: {{ result.usage.output_tokens }}
                          </div>
                        </div>
                        
                        <!-- JSON complet -->
                        <details class="response-details">
                          <summary>Voir la réponse JSON complète</summary>
                          <div class="response-json">
                            <code>{{ JSON.stringify(result.response, null, 2) }}</code>
                          </div>
                        </details>
                      </div>
                      <div v-else class="response-error">
                        {{ result.error }}
                      </div>
                    </div>
                    <div class="test-timestamp">
                      {{ formatDate(result.timestamp) }}
                    </div>
                  </div>
                </div>
                <button class="btn-clear-tests" @click="clearTestResults">
                  Effacer les résultats
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Products Catalog Section -->
      <div class="products-section">
        <div class="products-card">
          <div class="card-header">
            <div class="card-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 7V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v1"></path>
                <path d="M21 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7"></path>
                <path d="M8 2h8"></path>
                <path d="M21 7L12 14 3 7"></path>
              </svg>
            </div>
            <div class="card-title">
              <h3>Catalogue Produits</h3>
              <p>Intégrer les produits du catalogue e-commerce dans la base de connaissances</p>
            </div>
          </div>
          
          <div class="products-content">
            <!-- Products Stats -->
            <div class="products-stats-top" v-if="availableProducts.length > 0">
              <div class="stats-grid">
                <div class="stat-card available">
                  <div class="stat-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M21 7V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v1"></path>
                      <path d="M21 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7"></path>
                    </svg>
                  </div>
                  <div class="stat-content">
                    <span class="stat-number">{{ availableProducts.length }}</span>
                    <span class="stat-label">Produits disponibles</span>
                  </div>
                </div>
                
                <div class="stat-card selected">
                  <div class="stat-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M9 12l2 2 4-4"/>
                      <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9"/>
                    </svg>
                  </div>
                  <div class="stat-content">
                    <span class="stat-number">{{ selectedProducts.length }}</span>
                    <span class="stat-label">Produits sélectionnés</span>
                  </div>
                </div>
                
                <div class="stat-card processed">
                  <div class="stat-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                  <div class="stat-content">
                    <span class="stat-number">{{ productsStats.totalValue }} XOF</span>
                    <span class="stat-label">Valeur totale</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Products Filter and Selection -->
            <div class="products-filter-section" v-if="availableProducts.length > 0">
              <div class="filter-controls">
                <div class="search-products">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="M21 21l-4.35-4.35"></path>
                  </svg>
                  <input 
                    type="text" 
                    placeholder="Rechercher des produits..." 
                    v-model="productsSearchQuery"
                    @input="filterProducts"
                  />
                </div>
                <div class="selection-actions">
                  <button class="btn-select-all" @click="selectAllProducts">
                    Tout sélectionner ({{ selectedProducts.length }}/{{ filteredProducts.length }})
                  </button>
                  <button class="btn-clear-selection" @click="clearSelectedProducts" v-if="selectedProducts.length > 0">
                    Tout désélectionner
                  </button>
                </div>
              </div>
              
              <!-- Products List -->
              <div class="products-list" v-if="filteredProducts.length > 0">
                <div 
                  v-for="product in filteredProducts" 
                  :key="product._id" 
                  class="product-item"
                  :class="{ 'selected': isProductSelected(product._id) }"
                >
                  <div class="product-checkbox">
                    <input 
                      type="checkbox" 
                      :id="'product-' + product._id"
                      :value="product._id"
                      v-model="selectedProducts"
                    />
                    <label :for="'product-' + product._id" class="checkbox-label"></label>
                  </div>
                  
                  <div class="product-info">
                    <div class="product-header">
                      <h4 class="product-name">{{ product.title }}</h4>
                      <span class="product-price">{{ formatPrice(product.price, product.currency) }}</span>
                    </div>
                    <div class="product-description" v-if="product.description">
                      {{ truncateText(product.description, 150) }}
                    </div>
                    <div class="product-meta">
                      <span class="product-category">{{ product.category }}</span>
                      <span class="product-stock">Stock: {{ product.stock }}</span>
                      <span class="product-id">ID: {{ product.product_retailer_id }}</span>
                    </div>
                  </div>
                  
                  <div class="product-image" v-if="product.images && product.images[0]">
                    <img :src="product.images[0]" :alt="product.title" />
                  </div>
                </div>
              </div>

              <div v-else class="no-products-found">
                <p>Aucun produit trouvé avec ces critères</p>
              </div>
            </div>
            
            <!-- Loading Products -->
            <div v-if="isLoadingProducts" class="loading-products">
              <div class="loading-spinner"></div>
              <p>Chargement des produits...</p>
            </div>
            
            <!-- No Products -->
            <div v-else-if="availableProducts.length === 0" class="no-products-available">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                <path d="M21 7V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v1"></path>
                <path d="M21 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7"></path>
              </svg>
              <h3>Aucun produit disponible</h3>
              <p>Aucun produit trouvé dans le catalogue e-commerce</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Upload and Knowledge Library Section -->
      <div class="upload-library-section">
        <!-- Upload Section -->
        <div class="upload-card">
          <div class="upload-area" 
               :class="{ 'drag-over': isDragOver, 'uploading': isUploading }"
               @drop="handleDrop"
               @dragover.prevent="isDragOver = true"
               @dragleave="isDragOver = false"
               @click="triggerFileInput">
            
            <div v-if="!isUploading" class="upload-content">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                <line x1="12" y1="22.08" x2="12" y2="12"></line>
              </svg>
              <h3>Glissez vos fichiers ici ou cliquez pour parcourir</h3> 
              <p>Formats supportés : PDF, Images (JPEG, PNG), Texte (TXT, DOC, DOCX)</p>
              <p class="size-limit">Maximum 10MB par fichier - 10 fichiers maximum</p>
            </div>

            <div v-else class="upload-progress">
              <div class="progress-circle">
                <div class="progress-value">{{ uploadProgress }}%</div>
              </div>
              <p>Upload en cours...</p>
            </div>
          </div>

          <input 
            ref="fileInput" 
            type="file" 
            multiple 
            style="display: none"
            accept="image/jpeg,image/png,application/pdf,text/plain,.doc,.docx"
            @change="handleFileSelect"
            :disabled="isUploading"
          />

          <!-- Selected Files Preview -->
          <div v-if="selectedFiles.length > 0" class="selected-files">
            <h4>Fichiers sélectionnés ({{ selectedFiles.length }})</h4>
            <div class="files-grid">
              <div v-for="(file, index) in selectedFiles" :key="index" class="file-preview">
                <div class="file-icon">
                  <svg v-if="file.type.includes('pdf')" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                  </svg>
                  <svg v-else-if="file.type.includes('image')" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                  </svg>
                  <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6,2C4.89,2 4,2.89 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2H6Z"/>
                  </svg>
                </div>
                <div class="file-info">
                  <div class="file-name">{{ file.name }}</div>
                  <div class="file-size">{{ formatFileSize(file.size) }}</div>
                </div>
                <button class="remove-file" @click="removeSelectedFile(index)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>
            
            <div class="upload-actions">
              <button class="btn-cancel" @click="clearSelectedFiles" :disabled="isUploading">
                Annuler
              </button>
              <button class="btn-upload" @click="uploadFiles" :disabled="isUploading || selectedFiles.length === 0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                {{ isUploading ? 'Upload en cours...' : 'Uploader les fichiers' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Knowledge Library -->
        <div class="knowledge-library">
          <div class="library-header">
            <h2>Bibliothèque de Connaissances</h2>
            <div class="library-actions">
              <div class="search-box">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="M21 21l-4.35-4.35"></path>
                </svg>
                <input 
                  type="text" 
                  placeholder="Rechercher dans les fichiers..." 
                  v-model="searchQuery"
                  @input="filterFiles"
                />
              </div>
              <select v-model="selectedFilter" @change="filterFiles" class="filter-select">
                <option value="all">Tous les fichiers</option>
                <option value="images">Images</option>
                <option value="documents">Documents</option>
                <option value="text">Texte</option>
              </select>
            </div>
          </div>

          <div v-if="isLoadingFiles" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Chargement des fichiers...</p>
          </div>

          <div v-else-if="filteredFiles.length === 0" class="empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
            </svg>
            <h3>Aucun fichier trouvé</h3>
            <p>Commencez par uploader des fichiers pour enrichir votre base de connaissances</p>
          </div>

          <div v-else class="files-grid-library">
            <div v-for="file in filteredFiles" :key="file.id" class="file-card">
              <div class="file-header">
                <div class="file-type-badge" :class="getFileTypeClass(file.mimetype)">
                  {{ getFileTypeLabel(file.mimetype) }}
                </div>
                <div class="file-actions">
                  <button @click="previewFile(file)" class="action-btn preview-btn" title="Prévisualiser">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  </button>
                  <button @click="deleteFile(file)" class="action-btn delete-btn" title="Supprimer">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>
              </div>
              
              <div class="file-content">
                <div class="file-icon-large">
                  <svg v-if="file.mimetype.includes('pdf')" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v1h-1.5V7h3v1.5zM9 9.5h1v-1H9v1z"/>
                  </svg>
                  <img v-else-if="file.mimetype.includes('image')" :src="getFileUrl(file)" :alt="file.originalname" />
                  <svg v-else width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6,2C4.89,2 4,2.89 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2H6Z"/>
                  </svg>
                </div>
                
                <div class="file-info-detailed">
                  <h4 class="file-name-detailed">{{ file.originalname }}</h4>
                  <div class="file-metadata">
                    <span class="file-size-meta">{{ formatFileSize(file.size) }}</span>
                    <span class="file-date">{{ formatDate(file.uploaded_at) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Global Collect Button -->
    <div class="global-collect-section">
      <div class="collect-container">
        <div class="collect-info">
          <h3>Collecter toutes les connaissances</h3>
          <p>Intégrer les blocs sélectionnés, fichiers uploadés et produits du catalogue dans la base de connaissances IA</p>
          <div class="collect-summary">
            <span class="summary-item">
              <strong>{{ selectedBlocks.length }}</strong> bloc(s)
            </span>
            <span class="summary-item">
              <strong>{{ uploadedFiles.length }}</strong> fichier(s)
            </span>
            <span class="summary-item">
              <strong>{{ selectedProducts.length }}</strong> produit(s)
            </span>
          </div>
        </div>
        
        <button 
          class="btn-global-collect" 
          @click="startGlobalCollection" 
          :disabled="isCollecting || (selectedBlocks.length === 0 && uploadedFiles.length === 0 && selectedProducts.length === 0)"
          :class="{ 'collecting': isCollecting }"
        >
          <svg v-if="!isCollecting" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
            <line x1="12" y1="22.08" x2="12" y2="12"></line>
          </svg>
          <svg v-else class="spinning" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 12a9 9 0 11-6.219-8.56"></path>
          </svg>
          {{ isCollecting ? 'Génération en cours...' : 'Collecter les connaissances' }}
        </button>
      </div>
      
      <div v-if="globalCollectionProgress.total > 0" class="global-collection-progress">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: (globalCollectionProgress.current / globalCollectionProgress.total * 100) + '%' }"></div>
        </div>
        <div class="progress-text">
          {{ globalCollectionProgress.current }} / {{ globalCollectionProgress.total }} éléments traités
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import { axiosPeelo } from '@/utils/axios';
import PageHeader from '@/components/PageHeader.vue';
import { ClaudePromptGenerator, createClaudeIntegration } from '@/utils/claudePromptGenerator.js'
import { apiBaseUrl } from '@/config'

export default {
  name: 'JarvisKnowledgeView',
  components: {
    PageHeader
  },
  
  data() {
    return {
      // Notification system
      notification: {
        show: false,
        message: '',
        type: 'success',
        timeout: null
      },
      
      // Bot data
      chatbot: null,
      defaultBlock: {
        jarvis: false
      },
      
      // Knowledge statistics
      knowledgeStats: {
        totalFiles: 0,
        totalSize: '0 MB',
        documentsCount: 0,
        imagesCount: 0
      },
      
      // Upload functionality
      selectedFiles: [],
      isUploading: false,
      uploadProgress: 0,
      isDragOver: false,
      
      // File management
      uploadedFiles: [],
      filteredFiles: [],
      isLoadingFiles: false,
      searchQuery: '',
      selectedFilter: 'all',
      
      // Scraping and Blocks functionality
      isScraping: false,
      scrapingStats: {
        totalBlocks: 0,
        scrapedBlocks: 0
      },
      scrapingProgress: {
        current: 0,
        total: 0
      },
      
      // Blocks management
      availableBlocks: [],
      filteredBlocks: [],
      selectedBlocks: [],
      blocksSearchQuery: '',
      flowDataCache: null,
      
      // Products management
      availableProducts: [],
      filteredProducts: [],
      selectedProducts: [],
      productsSearchQuery: '',
      isLoadingProducts: false,
      productsStats: {
        totalValue: 0
      },
      
      // Global collection
      isCollecting: false,
      globalCollectionProgress: {
        current: 0,
        total: 0
      },
      
      // Claude AI Integration
      claudeIntegration: {
        isProcessing: false,
        systemPrompt: '',
        flowData: null,
        promptStats: {
          totalBlocks: 0,
          activeBlocks: 0,
          promptLength: 0
        },
        testResults: [],
        lastGenerated: null,
        error: null
      },
      
      // Prompt Display and Testing
      showPromptContent: false,
      editablePrompt: '',
      originalPrompt: '',
      testMessage: '',
      isTesting: false,
      testResults: [],
      isSavingPrompt: false,
      
      // Configuration
      maxFileSize: 10 * 1024 * 1024, // 10MB
      maxFiles: 10,
      allowedTypes: [
        'image/jpeg',
        'image/png', 
        'application/pdf',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ]
    }
  },
  
  computed: {
    chatbotId() {
      return this.$route.params.idchatbot
    },
    
    chatbotAvatarUrl() {
      if (this.chatbot?.avatar) {
        return `${apiBaseUrl}/public/${this.chatbot.avatar}`
      }
      return null
    },
    
    hasPromptChanged() {
      return this.editablePrompt !== this.originalPrompt && this.editablePrompt.trim() !== ''
    }
  },

  watch: {
    selectedProducts: {
      handler() {
        this.updateProductsStats()
      },
      deep: true
    }
  },
  
  async mounted() {
    await this.initializeKnowledge()
  },
  
  beforeUnmount() {
    if (this.notification.timeout) {
      clearTimeout(this.notification.timeout)
    }
  },
  
  methods: {
    // Initialization
    async initializeKnowledge() {
      try {
        await Promise.all([
          this.loadChatbotInfo(),
          this.loadDefaultBlock(),
          this.loadKnowledgeFiles(),
          this.loadScrapingStats(),
          this.loadProducts()
        ])
        this.updateKnowledgeStats()
      } catch (error) {
        console.error('Erreur lors de l\'initialisation:', error)
        this.showNotification({
          message: 'Erreur lors du chargement des données',
          type: 'error'
        })
      }
    },
    
    // Navigation
    goBack() {
      if (this.$route.params.from === 'playground') {
        this.$router.push(`/dashboard/chatbots/${this.chatbotId}/playground`)
      } else {
        this.$router.push(`/dashboard/chatbots/${this.chatbotId}/home`)
      }
    },
    
    // Load chatbot information
    async loadChatbotInfo() {
      consoleLog('loadChatbotInfo')
      try {
        const userData = localStorage.getItem('user')
        if (!userData) throw new Error('Données utilisateur non trouvées')
        
        const userObj = JSON.parse(userData)
        const token = userObj.data.token
        
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
        consoleLog('config===>',config)

        const response = await axiosPeelo.get(
          `/get/strong/chatbot/${this.chatbotId}`,
          config
        )


        consoleLog('Informations du chatbot chargées:response.data===>',response.data)
        consoleLog('Informations du chatbot chargées:response.data===>', JSON.stringify(response.data) )

        if (response.data && response.data?.chatbot) {
          this.chatbot = response.data.chatbot
          consoleLog('Informations du chatbot chargées:', this.chatbot)
        }
        
      } catch (error) {
        console.error('Erreur lors du chargement des infos du chatbot:', error)
        this.showNotification({
          message: 'Erreur lors du chargement des informations du chatbot',
          type: 'error'
        })
      }
    },
    
    // Load default block
    async loadDefaultBlock() {
      try {
        const userData = localStorage.getItem('user')
        if (!userData) throw new Error('Données utilisateur non trouvées')
        
        const userObj = JSON.parse(userData)
        const token = userObj.data.token
        
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
        
        const response = await axiosPeelo.get(
          `/strong/get/${this.chatbotId}/default`,
          config
        )
        
        if (response.data && response.data.success && response.data.defaultblock) {
          this.defaultBlock = response.data.defaultblock || { jarvis: false }
          consoleLog('Default block chargé:', this.defaultBlock)
        }
        
      } catch (error) {
        consoleError('Erreur lors du chargement du default block:', error)
        this.defaultBlock = { jarvis: false }
      }
    },
    
    // File Upload Handlers
    triggerFileInput() {
      if (!this.isUploading) {
        this.$refs.fileInput.click()
      }
    },
    
    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      this.processSelectedFiles(files)
      
      // Reset input
      event.target.value = ''
    },
    
    handleDrop(event) {
      event.preventDefault()
      this.isDragOver = false
      
      if (this.isUploading) return
      
      const files = Array.from(event.dataTransfer.files)
      this.processSelectedFiles(files)
    },
    
    processSelectedFiles(files) {
      const validFiles = []
      const errors = []
      
      // Check total files limit
      if (this.selectedFiles.length + files.length > this.maxFiles) {
        errors.push(`Maximum ${this.maxFiles} fichiers autorisés`)
      }
      
      files.forEach(file => {
        // Check file type
        if (!this.allowedTypes.includes(file.type)) {
          errors.push(`${file.name}: Type de fichier non autorisé`)
          return
        }
        
        // Check file size
        if (file.size > this.maxFileSize) {
          errors.push(`${file.name}: Fichier trop volumineux (max 10MB)`)
          return
        }
        
        // Check for duplicates
        if (this.selectedFiles.some(f => f.name === file.name && f.size === file.size)) {
          errors.push(`${file.name}: Fichier déjà sélectionné`)
          return
        }
        
        validFiles.push(file)
      })
      
      // Add valid files
      this.selectedFiles.push(...validFiles)
      
      // Show errors if any
      if (errors.length > 0) {
        this.showNotification({
          message: errors.join(', '),
          type: 'error'
        })
      }
      
      // Show success message
      if (validFiles.length > 0) {
        this.showNotification({
          message: `${validFiles.length} fichier(s) sélectionné(s)`,
          type: 'success'
        })
      }
    },
    
    removeSelectedFile(index) {
      this.selectedFiles.splice(index, 1)
    },
    
    clearSelectedFiles() {
      this.selectedFiles = []
    },
    
    async uploadFiles() {
      if (this.selectedFiles.length === 0 || this.isUploading) return
      
      this.isUploading = true
      this.uploadProgress = 0
      
      try {
        const userData = localStorage.getItem('user')
        if (!userData) {
          throw new Error('Données utilisateur non trouvées')
        }
        
        const userObj = JSON.parse(userData)
        const token = userObj.data.token
        
        const formData = new FormData()
        this.selectedFiles.forEach(file => {
          formData.append('files', file)
        })
         
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 120000, // 2 minutes
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
          }
        }
          
        // New Endpoint: POST /upload/knowledge/jarvis/:chatbotId
        const response = await axios.post(
          `https://jarvis.aipeelo.xyz/upload/knowledge/jarvis/${this.chatbotId}`,
          formData,
          config 
        )
        
        if (response.data && response.data.success) {
          this.showNotification({
            message: response.data.message || 'Fichiers uploadés avec succès',
            type: 'success'
          })
          
          // Clear selected files
          this.selectedFiles = []
          
          // Reload knowledge files
          await this.loadKnowledgeFiles()
          this.updateKnowledgeStats()
          
        } else {
          throw new Error(response.data?.message || 'Erreur lors de l\'upload')
        }
        
      } catch (error) {
        consoleError('Erreur upload:', error)
        
        let errorMessage = 'Erreur lors de l\'upload des fichiers'
        
        if (error.response) {
          switch (error.response.status) {
            case 401:
              errorMessage = 'Token d\'authentification invalide'
              break
            case 400:
              errorMessage = error.response.data?.message || 'Requête invalide'
              break
            case 413:
              errorMessage = 'Fichier trop volumineux'
              break
            case 500:
              errorMessage = 'Erreur serveur lors de l\'upload'
              break
          }
        } else if (error.code === 'ECONNABORTED') {
          errorMessage = 'Timeout - Fichier trop volumineux ou connexion lente'
        }
        
        this.showNotification({
          message: errorMessage,
          type: 'error'
        })
        
      } finally {
        this.isUploading = false
        this.uploadProgress = 0
      }
    },
    
    // Knowledge Management
    async loadKnowledgeFiles() {
      this.isLoadingFiles = true
      
      try {
        const userData = localStorage.getItem('user')
        if (!userData) throw new Error('Données utilisateur non trouvées')
        
        const userObj = JSON.parse(userData)
        const token = userObj.data.token
        
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
        
        // Updated Endpoint: GET /upload/knowledge/jarvis/:chatbotId/documents
        const response = await axios.get(
          `https://jarvis.aipeelo.xyz/upload/knowledge/jarvis/${this.chatbotId}/documents`,
          config
        )
        
        if (response.data && response.data.success) {
          // Map response data to internal format
          // API returns: { success: true, data: { documents: [...] } }
          const docs = response.data.data?.documents || []
          
          this.uploadedFiles = docs.map(doc => ({
            id: doc.filename, // Use filename as unique ID if no other ID provided
            index: doc.index, // Important for deletion
            name: doc.name,
            originalname: doc.name,
            filename: doc.filename,
            mimetype: doc.mimetype,
            size: doc.size,
            url: doc.url,
            uploadDate: doc.uploadDate
          }))
          
          this.filterFiles()
        }
        
      } catch (error) {
        consoleError('Erreur lors du chargement des fichiers:', error)
        this.uploadedFiles = []
      } finally {
        this.isLoadingFiles = false
      }
    },
    
    filterFiles() {
      let filtered = [...this.uploadedFiles]
      
      // Filter by type
      if (this.selectedFilter !== 'all') {
        filtered = filtered.filter(file => {
          switch (this.selectedFilter) {
            case 'images':
              return file.mimetype.includes('image')
            case 'documents':
              return file.mimetype.includes('pdf') || 
                     file.mimetype.includes('word') ||
                     file.mimetype.includes('document')
            case 'text':
              return file.mimetype.includes('text')
            default:
              return true
          }
        })
      }
      
      // Filter by search query
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(file => 
          file.originalname.toLowerCase().includes(query)
        )
      }
      
      this.filteredFiles = filtered
    },
    
    async deleteFile(file) {
      if (!confirm(`Êtes-vous sûr de vouloir supprimer "${file.originalname}" ?`)) {
        return
      }
      
      try {
        const userData = localStorage.getItem('user')
        if (!userData) throw new Error('Session expirée')
        
        const userObj = JSON.parse(userData)
        const token = userObj.data.token
        
        // Updated Endpoint: DELETE /upload/knowledge/jarvis/:chatbotId/documents/:documentIndex
        const response = await axios.delete(
          `https://jarvis.aipeelo.xyz/upload/knowledge/jarvis/${this.chatbotId}/documents/${file.index}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )
        
        if (response.data.success) {
          this.showNotification({
            message: 'Fichier supprimé avec succès',
            type: 'success'
          })
          
          // Reload files
          await this.loadKnowledgeFiles()
          this.updateKnowledgeStats()
          
        } else {
          this.showNotification({
            message: response.data.message || 'Erreur lors de la suppression du fichier',
            type: 'error'
          })
        }
      } catch (error) {
        consoleError('Erreur lors la suppression:', error)
        this.showNotification({
          message: 'Erreur lors de la suppression du fichier',
          type: 'error'
        })
      }
    },
    
    previewFile(file) {
      if (file.url) {
        window.open(file.url, '_blank')
      } else {
        this.showNotification({
          message: 'Impossible de prévisualiser ce fichier',
          type: 'error'
        })
      }
    },
    
    getFileUrl(file) {
      const baseUrl = process.env.VUE_APP_API_BASE_URL || 'https://api.peelo.io'
      return `${baseUrl}/${file.path}`
    },
    
    // Scraping functionality
    async loadScrapingStats() {
      consoleLog('loadScrapingStats')
      try {
        const userData = localStorage.getItem('user')
        if (!userData) throw new Error('Données utilisateur non trouvées')
        
        const userObj = JSON.parse(userData)
        const token = userObj.data.token
        
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
         
        const response = await axiosPeelo.get(
          `/strong/get/chatbot/${this.chatbotId}/flowall`,
          config
        )
        consoleLog('flowData ALL ===>', JSON.stringify(response.data))
        
        if (response.data && response.data.success) {
          // Stocker les données de flow pour manipulation
          this.flowDataCache = response.data
          
          // Extraire et préparer la liste des blocs
          this.extractAvailableBlocks(response.data.flow)
          
          let totalBlocks = this.availableBlocks.length
          this.scrapingStats.totalBlocks = totalBlocks
          
        }
        
      } catch (error) {
        consoleError('Erreur lors du chargement des stats de scraping:', error)
      }
    },

    // Extract and prepare blocks for selection
    extractAvailableBlocks(flowData) {
      const blocks = []
      
      // Add main blocks
      if (flowData.mainblocks) {
        Object.entries(flowData.mainblocks).forEach(([type, blockArray]) => {
          if (Array.isArray(blockArray)) {
            blockArray.forEach(block => {
              blocks.push({
                ...block,
                blockname: `${type.charAt(0).toUpperCase() + type.slice(1)} Block`,
                type: type,
                source: 'mainblocks',
                contentPreview: 'Bloc principal du chatbot',
                elementsCount: 0
              })
            })
          }
        })
      }
      
      // Add regular blocks
      if (flowData.blocks && Array.isArray(flowData.blocks)) {
        flowData.blocks.forEach(block => {
          const contentPreview = this.generateBlockPreview(block)
          const elementsCount = block.flow ? block.flow.length : 0
          
          blocks.push({
            ...block,
            source: 'blocks',
            contentPreview,
            elementsCount
          })
        })
      }
      
      this.availableBlocks = blocks
      this.filteredBlocks = [...blocks]
      
      // Select all blocks by default
      this.selectedBlocks = blocks.map(block => block.idblock)
    },

    // Generate preview text for a block
    generateBlockPreview(block) {
      if (!block.flow || block.flow.length === 0) {
        return 'Bloc vide'
      }
      
      const textElements = block.flow.filter(element => 
        element.type === 'text' || element.type === 'text_button'
      )
      
      if (textElements.length === 0) {
        return `${block.flow.length} élément(s) sans texte`
      }
      
      const firstText = textElements[0].message || ''
      return firstText.length > 100 ? 
        firstText.substring(0, 100) + '...' : 
        firstText
    },

    // Blocks filtering and selection
    filterBlocks() {
      let filtered = [...this.availableBlocks]
      
      if (this.blocksSearchQuery.trim()) {
        const query = this.blocksSearchQuery.toLowerCase()
        filtered = filtered.filter(block => 
          (block.blockname || '').toLowerCase().includes(query) ||
          (block.idblock || '').toLowerCase().includes(query) ||
          (block.contentPreview || '').toLowerCase().includes(query)
        )
      }
      
      this.filteredBlocks = filtered
    },

    selectAllBlocks() {
      this.selectedBlocks = this.filteredBlocks.map(block => block.idblock)
    },

    clearSelectedBlocks() {
      this.selectedBlocks = []
    },

    isBlockSelected(blockId) {
      return this.selectedBlocks.includes(blockId)
    },

    // Products management
    async loadProducts() {
      this.isLoadingProducts = true
      
      try {
        const response = await axios.get(
          `https://apicommerce.aipeelo.xyz/api/chatbots/${this.chatbotId}/products`
        )
        
        if (response.data && response.data.success) {
          this.availableProducts = response.data.products || []
          this.filterProducts()
          this.updateProductsStats()
          
          // Select all products by default
          this.selectedProducts = this.availableProducts.map(product => product._id)
          
          console.log('Produits chargés:', this.availableProducts.length)
        }
        
      } catch (error) {
        console.error('Erreur lors du chargement des produits:', error)
        this.availableProducts = []
        this.showNotification({
          message: 'Erreur lors du chargement des produits du catalogue',
          type: 'error'
        })
      } finally {
        this.isLoadingProducts = false
      }
    },

    filterProducts() {
      let filtered = [...this.availableProducts]
      
      if (this.productsSearchQuery.trim()) {
        const query = this.productsSearchQuery.toLowerCase()
        filtered = filtered.filter(product => 
          (product.title || '').toLowerCase().includes(query) ||
          (product.description || '').toLowerCase().includes(query) ||
          (product.category || '').toLowerCase().includes(query)
        )
      }
      
      this.filteredProducts = filtered
    },

    selectAllProducts() {
      this.selectedProducts = this.filteredProducts.map(product => product._id)
      this.updateProductsStats()
    },

    clearSelectedProducts() {
      this.selectedProducts = []
      this.updateProductsStats()
    },

    isProductSelected(productId) {
      return this.selectedProducts.includes(productId)
    },

    updateProductsStats() {
      const selectedProductsData = this.availableProducts.filter(
        product => this.selectedProducts.includes(product._id)
      )
      
      const totalValue = selectedProductsData.reduce((sum, product) => {
        return sum + (product.price || 0)
      }, 0)
      
      this.productsStats = {
        totalValue: Math.round(totalValue)
      }
    },

    formatPrice(price, currency = 'XOF') {
      if (!price) return '0 ' + currency
      return new Intl.NumberFormat('fr-FR').format(price) + ' ' + currency
    },

    truncateText(text, maxLength = 150) {
      if (!text) return ''
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    },

    // Claude AI Integration - Génération optimisée du system prompt
    async generateClaudePrompt(flowData = null) {
      console.log('🤖 Génération du prompt Claude optimisé... generateClaudePrompt() ', JSON.stringify({
        timestamp: new Date().toISOString(),
        chatbotId: this.chatbotId,
        selectedBlocks: this.selectedBlocks.length
      }))
      
      try {
        // Use provided flowData or cached data
        const dataToUse = flowData || this.flowDataCache

        console.log(`
      
          =========== DATA USE SCRAPIGGGG=======

        `, JSON.stringify(dataToUse, null, 2) )
        
        
        if (!dataToUse || !dataToUse.success) {
          throw new Error('Données de flow invalides')
        }

        // Filter blocks based on selection
        const filteredFlowData = this.createFilteredFlowData(dataToUse)

        console.log('filteredFlowData ==> ', JSON.stringify(filteredFlowData, null, 2) )
        return false;
        // Générer le prompt système optimisé avec la nouvelle fonction
        const systemPrompt = ClaudePromptGenerator.generateOptimizedSystemPrompt(filteredFlowData)
        
        // Calculer les tokens et coûts pour les logs
        const tokenCount = ClaudePromptGenerator.estimateTokenCount(systemPrompt)
        const costReport = ClaudePromptGenerator.generateCostReport(systemPrompt)
        
        console.log('📝 Prompt système optimisé généré:', {
          length: systemPrompt.length,
          estimatedTokens: tokenCount,
          avgTokensPerChar: costReport.summary.avgTokensPerChar,
          chatbotName: filteredFlowData.chatbotInfo?.bot_name || 'Unknown',
          blocksCount: this.selectedBlocks.length,
          costEstimates: {
            claudeHaiku: costReport.models.find(m => m.id.includes('haiku'))?.scenarios.medium.totalCost,
            claudeSonnet: costReport.models.find(m => m.id.includes('sonnet'))?.scenarios.medium.totalCost,
            claudeOpus: costReport.models.find(m => m.id.includes('opus'))?.scenarios.medium.totalCost
          }
        })
        
        console.log('✅ Prompt Claude optimisé généré avec succès')
        
        return systemPrompt
        
      } catch (error) {
        console.error('❌ Erreur lors de la génération du prompt Claude:', error)
        this.showNotification({
          message: `Erreur génération Claude: ${error.message}`,
          type: 'error'
        })
        throw error
      }
    },

    // Generate Claude prompt with products included
    async generateClaudePromptWithProducts() { 
      console.log('🤖 Génération du prompt Claude avec produits...', JSON.stringify({
        timestamp: new Date().toISOString(),
        chatbotId: this.chatbotId,
        selectedBlocks: this.selectedBlocks.length,
        selectedProducts: this.selectedProducts.length
      }))
      
      try {
        if (!this.flowDataCache || !this.flowDataCache.success) {
          throw new Error('Données de flow invalides')
        }

        // Filter blocks based on selection
        const filteredFlowData = this.createFilteredFlowData(this.flowDataCache)
        
        // Add products data to flow data
        const selectedProductsData = this.availableProducts.filter(
          product => this.selectedProducts.includes(product._id)
        )
        
        // Add products section to the flow data
        filteredFlowData.products = {
          catalog: selectedProductsData,
          totalProducts: selectedProductsData.length,
          categories: [...new Set(selectedProductsData.map(p => p.category))],
          totalValue: selectedProductsData.reduce((sum, p) => sum + (p.price || 0), 0)
        }
        
        // Générer le prompt système optimisé avec les produits
        const systemPrompt = ClaudePromptGenerator.generateOptimizedSystemPrompt(filteredFlowData)
        
        // Calculer les tokens et coûts pour les logs
        const tokenCount = ClaudePromptGenerator.estimateTokenCount(systemPrompt)
        const costReport = ClaudePromptGenerator.generateCostReport(systemPrompt)
        
        console.log('📝 Prompt système avec produits généré:', {
          length: systemPrompt.length,
          estimatedTokens: tokenCount,
          avgTokensPerChar: costReport.summary.avgTokensPerChar,
          chatbotName: filteredFlowData.chatbotInfo?.bot_name || 'Unknown',
          blocksCount: this.selectedBlocks.length,
          productsCount: this.selectedProducts.length,
          costEstimates: {
            claudeHaiku: costReport.models.find(m => m.id.includes('haiku'))?.scenarios.medium.totalCost,
            claudeSonnet: costReport.models.find(m => m.id.includes('sonnet'))?.scenarios.medium.totalCost,
            claudeOpus: costReport.models.find(m => m.id.includes('opus'))?.scenarios.medium.totalCost
          }
        })
        
        console.log('✅ Prompt Claude avec produits généré avec succès')
        
        return systemPrompt
        
      } catch (error) {
        console.error('❌ Erreur lors de la génération du prompt Claude avec produits:', error)
        this.showNotification({
          message: `Erreur génération Claude avec produits: ${error.message}`,
          type: 'error'
        })
        throw error
      }
    },

    // Create filtered flow data based on selected blocks
    createFilteredFlowData(originalFlowData) {
      console.log('createFilteredFlowData ===>')
      const filteredData = JSON.parse(JSON.stringify(originalFlowData)) // Deep clone
      console.log(`filteredData.flow.mainblocks TAILLE : ${filteredData.flow.mainblocks.length}`)
      
      // Filter main blocks
      if (filteredData.flow.mainblocks) {
        Object.keys(filteredData.flow.mainblocks).forEach(key => {
          filteredData.flow.mainblocks[key] = filteredData.flow.mainblocks[key].filter(
            block => this.selectedBlocks.includes(block.idblock)
          )
        })
      }

      console.log(`filteredData.flow.blocks TAILLE : ${filteredData.flow.blocks.length}`)

       // Filter regular blocks
      if (filteredData.flow.blocks) {
        filteredData.flow.blocks = filteredData.flow.blocks.filter(
          block => this.selectedBlocks.includes(block.idblock)
        )
      }
      console.log(`
      AFTER
      
      `)

      console.log(`filteredData.flow.blocks TAILLE : ${filteredData.flow.blocks.length}`)

      return filteredData
    },

    // Copy prompt to clipboard
    async copyPromptToClipboard() {
      try {
        await navigator.clipboard.writeText(this.claudeIntegration.systemPrompt)
        this.showNotification({
          message: 'Prompt copié dans le presse-papiers',
          type: 'success'
        })
      } catch (error) {
        console.error('Erreur lors de la copie:', error)
        this.showNotification({
          message: 'Erreur lors de la copie du prompt',
          type: 'error'
        })
      }
    },

    // Test du prompt Claude avec un message utilisateur via l'API Jarvis
    async testClaudePrompt(userMessage) {
      console.log('🧪 Test du prompt Claude via API Jarvis...', JSON.stringify({
        userMessage,
        chatbotId: this.chatbotId,
        hasPrompt: !!this.editablePrompt
      }))
      
      if (!this.editablePrompt) {
        throw new Error('Aucun prompt Claude généré')
      }
      
      try {

        const userData = localStorage.getItem('user')
        if (!userData) throw new Error('Données utilisateur non trouvées')
        
        const userObj = JSON.parse(userData)
        const token = userObj.data.token
        
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000 // 30 secondes timeout
        }
        
        // Envoyer la requête à l'API Jarvis externe
        const response = await axios.post(
          'https://jarvis.aipeelo.xyz/test/systemprompt',
          {
            userMessage: userMessage,
            idbot: this.chatbotId
          },
          config
        )
        
        console.log('✅ Réponse de l\'API Jarvis:', JSON.stringify(response.data))
        return response.data
        
      } catch (error) {
        console.error('❌ Erreur lors du test Claude:', error)
        
        // Gestion spécifique des erreurs de l'API externe
        if (error.response) {
          throw new Error(`API Jarvis Error (${error.response.status}): ${error.response.data?.message || error.response.statusText}`)
        } else if (error.request) {
          throw new Error('Impossible de joindre l\'API Jarvis - Vérifiez votre connexion')
        } else {
          throw new Error(error.message || 'Erreur lors du test Claude')
        }
      }
    },

    // Exporter le prompt Claude
    exportClaudePrompt() {
      if (!this.claudeIntegration.systemPrompt) {
        this.showNotification({
          message: 'Aucun prompt à exporter',
          type: 'error'
        })
        return
      }
      
      const exportData = {
        chatbotId: this.chatbotId,
        chatbotName: this.chatbot?.bot_name || 'Chatbot',
        generatedAt: this.claudeIntegration.lastGenerated,
        selectedBlocks: this.selectedBlocks,
        systemPrompt: this.claudeIntegration.systemPrompt,
        stats: this.claudeIntegration.promptStats,
        testExamples: this.claudeIntegration.testResults
      }
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      })
      
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `claude-prompt-${this.chatbotId}-${Date.now()}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
      this.showNotification({
        message: 'Prompt Claude exporté avec succès',
        type: 'success'
      })
    },

    // Toggle prompt display
    togglePromptDisplay() {
      this.showPromptContent = !this.showPromptContent
    },

    // Test the Claude prompt
    async testPrompt() {
      if (!this.testMessage.trim() || this.isTesting) return
      
      this.isTesting = true
      const message = this.testMessage.trim()
      
      try {
        const result = await this.testClaudePrompt(message)
        
        this.testResults.unshift({
          message: message,
          response: result,
          success: true,
          timestamp: new Date().toISOString()
        })
        
        // Afficher une notification en fonction du format de réponse Jarvis
        let notificationMessage = 'Test réussi'
        if (result && typeof result === 'object') {
          if (result.success) {
            if (result.response && result.response.redirection_block) {
              notificationMessage = `✅ Test réussi - Redirection vers: ${result.response.redirection_block}`
            } else if (result.response && result.response.type) {
              notificationMessage = `✅ Test réussi - Type: ${result.response.type}`
            } else {
              notificationMessage = `✅ Test réussi avec ${result.model || 'Claude'}`
            }
          } else {
            notificationMessage = `❌ Test échoué`
          }
        }
        
        this.showNotification({
          message: notificationMessage,
          type: result.success ? 'success' : 'warning'
        })
        
      } catch (error) {
        console.error('Erreur lors du test:', error)
        
        this.testResults.unshift({
          message: message,
          error: error.message,
          success: false,
          timestamp: new Date().toISOString()
        })
        
        this.showNotification({
          message: `Erreur lors du test: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.isTesting = false
        this.testMessage = ''
      }
    },

    // Clear test results
    clearTestResults() {
      this.testResults = []
    },

    // Prompt editing methods
    onPromptEdit() {
      // Le prompt est maintenant édité, on peut l'utiliser pour les tests
    },

    resetPrompt() {
      this.editablePrompt = this.originalPrompt
      this.showNotification({
        message: 'Prompt réinitialisé',
        type: 'success'
      })
    },

    formatPrompt() {
      // Simple formatage basique
      this.editablePrompt = this.editablePrompt
        .replace(/\n\n+/g, '\n\n') // Supprimer les lignes vides multiples
        .trim()
      
      this.showNotification({
        message: 'Prompt formaté',
        type: 'success'
      })
    },

    // Sauvegarder les modifications du prompt
    async savePromptChanges() {
      if (this.isSavingPrompt || !this.hasPromptChanged) return
      
      this.isSavingPrompt = true
      
      try {
        const userData = localStorage.getItem('user')
        if (!userData) throw new Error('Données utilisateur non trouvées')
        
        const userObj = JSON.parse(userData)
        const token = userObj.data.token
        
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
        
        // Envoyer seulement le prompt modifié et l'idbot
        const response = await axios.post(
          `https://jarvis.aipeelo.xyz/knowledge/jarvis/${this.chatbotId}/scrape-blocks`,
          {
            systemPrompt: this.editablePrompt,
            idbot: this.chatbotId
          },
          config
        )
        
        if (response.data && response.data.success) {
          this.showNotification({
            message: response.data.message || 'Prompt sauvegardé avec succès',
            type: 'success'
          })
          
          // Mettre à jour le prompt original pour refléter les modifications sauvegardées
          this.originalPrompt = this.editablePrompt
          this.claudeIntegration.systemPrompt = this.editablePrompt
          
          // Mettre à jour les statistiques
          this.claudeIntegration.promptStats = {
            ...this.claudeIntegration.promptStats,
            promptLength: this.editablePrompt.length
          }
          this.claudeIntegration.lastGenerated = response.data.ObjectSystem?.update_date || response.data.ObjectSystem?.created_at || new Date().toISOString()
          
          console.log('✅ Prompt sauvegardé avec succès:', {
            length: this.editablePrompt.length,
            timestamp: this.claudeIntegration.lastGenerated
          })
          
        } else {
          throw new Error(response.data?.message || 'Erreur lors de la sauvegarde')
        }
        
      } catch (error) {
        console.error('Erreur lors de la sauvegarde du prompt:', error)
        
        let errorMessage = 'Erreur lors de la sauvegarde du prompt'
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.showNotification({
          message: errorMessage,
          type: 'error'
        })
      } finally {
        this.isSavingPrompt = false
      }
    },

    // Global collection method
    async startGlobalCollection() {
      if (this.isCollecting) return
      
      const totalElements = this.selectedBlocks.length + this.uploadedFiles.length + this.selectedProducts.length
      
      if (totalElements === 0) {
        this.showNotification({
          message: 'Aucun élément sélectionné pour la collecte',
          type: 'warning'
        })
        return
      }
      
      this.isCollecting = true
      this.globalCollectionProgress = { current: 0, total: totalElements }
      
      try {
        // Générer le prompt Claude avec tous les éléments sélectionnés
        const systemPrompt = await this.generateClaudePromptWithProducts()
        
        const userData = localStorage.getItem('user')
        if (!userData) throw new Error('Données utilisateur non trouvées')
        
        const userObj = JSON.parse(userData)
        const token = userObj.data.token
        
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
        
        // Préparer les données à envoyer
        const collectData = {
          selectedBlocks: this.selectedBlocks,
          selectedProducts: this.selectedProducts,
          systemPrompt: systemPrompt,
          idbot: this.chatbotId
        }
         console.log('collectData ==>', JSON.stringify(collectData,null,2) )
        // Start global collection process
        const response = await axios.post(
          `https://jarvis.aipeelo.xyz/knowledge/jarvis/${this.chatbotId}/scrape-blocks`,
          collectData,
          config
        )
        
        if (response.data && response.data.success) {
          this.showNotification({
            message: response.data.message || `${totalElements} éléments traités avec succès`,
            type: 'success'
          })
          
          // Utiliser le prompt retourné par le serveur s'il existe
          const serverPrompt = response.data.ObjectSystem?.systemPrompt?.promptblock || systemPrompt
          
          if (serverPrompt) {
            // Stocker le prompt généré pour l'affichage
            this.claudeIntegration.systemPrompt = serverPrompt
            this.editablePrompt = serverPrompt
            this.originalPrompt = serverPrompt
            this.showPromptContent = true
            
            // Mettre à jour les statistiques du prompt
            const tokenCount = ClaudePromptGenerator.estimateTokenCount(serverPrompt)
            const costReport = ClaudePromptGenerator.generateCostReport(serverPrompt)
            
            this.claudeIntegration.promptStats = {
              activeBlocks: this.selectedBlocks.length,
              promptLength: serverPrompt.length,
              totalBlocks: this.availableBlocks.length,
              estimatedTokens: tokenCount,
              avgTokensPerChar: costReport.summary.avgTokensPerChar,
              costReport: costReport
            }
            this.claudeIntegration.lastGenerated = response.data.ObjectSystem?.created_at || new Date().toISOString()
            
            console.log('✅ Prompt système mis à jour avec produits:', {
              length: serverPrompt.length,
              activeBlocks: this.selectedBlocks.length,
              selectedProducts: this.selectedProducts.length
            })
          }
          
          // Reload knowledge files
          await this.loadKnowledgeFiles()
          this.updateKnowledgeStats()
        }
        
      } catch (error) {
        console.error('Erreur lors de la collecte globale:', error)
        
        let errorMessage = 'Erreur lors de la collecte des connaissances'
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.showNotification({
          message: errorMessage,
          type: 'error'
        })
      } finally {
        this.isCollecting = false
        this.globalCollectionProgress = { current: 0, total: 0 }
      }
    },
    
    async startScraping() {
      
      if (this.isScraping || this.selectedBlocks.length === 0) return
      
      this.isScraping = true
      this.scrapingProgress = { current: 0, total: this.selectedBlocks.length }
       
      console.log('this.scrapingProgress ==>',this.scrapingProgress)

      try {
        // Générer le prompt Claude avec les blocs sélectionnés
        const systemPrompt = await this.generateClaudePrompt()
        
        const userData = localStorage.getItem('user')
        if (!userData) throw new Error('Données utilisateur non trouvées')
        
        const userObj = JSON.parse(userData)
        const token = userObj.data.token
        
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
        
        // Start scraping process with selected blocks, systemPrompt and idbot
        const response = await axiosPeelo.post(
          `/knowledge/jarvis/${this.chatbotId}/scrape-blocks`,
          {
            selectedBlocks: this.selectedBlocks,
            systemPrompt: systemPrompt,
            idbot: this.chatbotId
          },
          config
        )
        
        if (response.data && response.data.success) {
          this.showNotification({
            message: response.data.message || `${this.selectedBlocks.length} blocs traités avec succès`,
            type: 'success'
          })
          
          // Utiliser le prompt retourné par le serveur s'il existe, sinon le prompt local généré
          const serverPrompt = response.data.ObjectSystem?.systemPrompt?.promptblock || systemPrompt
          
          if (!serverPrompt) {
            console.warn('Aucun prompt système reçu du serveur ni généré localement')
            this.showNotification({
              message: 'Attention: Aucun prompt système généré',
              type: 'warning'
            })
          } else {
            // Stocker le prompt généré pour l'affichage
            this.claudeIntegration.systemPrompt = serverPrompt
            this.editablePrompt = serverPrompt
            this.originalPrompt = serverPrompt
            this.showPromptContent = true
            
            // Mettre à jour les statistiques du prompt avec calcul de tokens
            const tokenCount = ClaudePromptGenerator.estimateTokenCount(serverPrompt)
            const costReport = ClaudePromptGenerator.generateCostReport(serverPrompt)
            
            this.claudeIntegration.promptStats = {
              activeBlocks: this.selectedBlocks.length,
              promptLength: serverPrompt.length,
              totalBlocks: this.availableBlocks.length,
              estimatedTokens: tokenCount,
              avgTokensPerChar: costReport.summary.avgTokensPerChar,
              costReport: costReport
            }
            this.claudeIntegration.lastGenerated = response.data.ObjectSystem?.created_at || new Date().toISOString()
            
            console.log('✅ Prompt système mis à jour:', {
              length: serverPrompt.length,
              source: response.data.ObjectSystem?.systemPrompt?.promptblock ? 'serveur' : 'local',
              activeBlocks: this.selectedBlocks.length
            })
          }
          
          // Mettre à jour les stats de scraping
          this.scrapingStats.scrapedBlocks = this.selectedBlocks.length
          
          // Reload knowledge files
          await this.loadKnowledgeFiles()
          this.updateKnowledgeStats()
        }
        
      } catch (error) {
        console.error('Erreur lors du scraping:', error)
        
        let errorMessage = 'Erreur lors de la collecte des données'
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.showNotification({
          message: errorMessage,
          type: 'error'
        })
      } finally {
        this.isScraping = false
        this.scrapingProgress = { current: 0, total: 0 }
      }
    },

    // Utility functions
    updateKnowledgeStats() {
      const totalFiles = this.uploadedFiles.length
      let totalSize = 0
      let documentsCount = 0
      let imagesCount = 0
      
      this.uploadedFiles.forEach(file => {
        totalSize += file.size || 0
        
        if (file.mimetype.includes('image')) {
          imagesCount++
        } else if (file.mimetype.includes('pdf') || 
                   file.mimetype.includes('word') ||
                   file.mimetype.includes('document')) {
          documentsCount++
        }
      })
      
      this.knowledgeStats = {
        totalFiles,
        totalSize: this.formatFileSize(totalSize),
        documentsCount,
        imagesCount
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },
    
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    getFileTypeClass(mimetype) {
      if (mimetype.includes('image')) return 'image'
      if (mimetype.includes('pdf')) return 'pdf'
      if (mimetype.includes('word') || mimetype.includes('document')) return 'document'
      if (mimetype.includes('text')) return 'text'
      return 'other'
    },
    
    getFileTypeLabel(mimetype) {
      if (mimetype.includes('image')) return 'Image'
      if (mimetype.includes('pdf')) return 'PDF'
      if (mimetype.includes('word') || mimetype.includes('document')) return 'Document'
      if (mimetype.includes('text')) return 'Texte'
      return 'Fichier'
    },
    
    showNotification({ message, type = 'success', duration = 5000 }) {
      if (this.notification.timeout) {
        clearTimeout(this.notification.timeout)
      }
      
      this.notification = {
        show: true,
        message,
        type,
        timeout: setTimeout(() => {
          this.notification.show = false
        }, duration)
      }
    },
    
    // Update Jarvis state
    async updateJarvisState() {
      console.log(' Dans updateJarvisState ', this.defaultBlock)
      try {
        const userData = localStorage.getItem('user')
        if (!userData) throw new Error('Session expirée')
        
        // Vérifier que defaultBlock existe
        if (!this.defaultBlock) {
          console.error('defaultBlock est undefined')
          this.showNotification({
            message: 'Erreur: Bloc par défaut non chargé',
            type: 'error'
          })
          return
        }
        
        const userObj = JSON.parse(userData)
        const token = userObj.data.token
        
        // Utiliser l'idblock du defaultBlock s'il existe, sinon utiliser le chatbotId
        const blockId = this.defaultBlock?.idblock || this.chatbotId
        console.log('Mise à jour Jarvis pour blockId:', blockId, 'defaultBlock:', this.defaultBlock)
        
        const response = await axiosPeelo.post(`/strong/update/jarvis/${blockId}`, {
          status: this.defaultBlock.jarvis
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.data.success) {
          this.showNotification({
            message: response.data.message || `GRIOT ${this.defaultBlock.jarvis ? 'activé' : 'désactivé'}`,
            type: 'success'
          })
        } else {
          this.showNotification({
            message: response.data.message || 'Erreur lors de la mise à jour de Jarvis',
            type: 'error'
          })
        }
      } catch (error) {
        this.showNotification({
          message: `Erreur: ${error.response?.data?.error || 'Erreur lors de la mise à jour de Jarvis'}`,
          type: 'error'
        })
        console.error('Erreur lors de la mise à jour de Jarvis:', error)
      }
    },
    
    // Handle image loading error
    onImageError(event) {
      console.warn('Erreur lors du chargement de l\'avatar:', event.target.src)
      // L'image ne peut pas être chargée, le fallback SVG s'affichera automatiquement
      // grâce à la condition v-if/v-else dans le template
    },


  }
}
</script>

<style scoped>
.jarvis-knowledge-container {
  min-height: 100vh;
  background: #f5f5f5;
  position: relative;
  overflow-x: hidden;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Notification */
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  color: white;
  font-weight: 500;
  z-index: 1000;
  animation: slideIn 0.3s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.notification.success {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
}

.notification.error {
  background: linear-gradient(135deg, #f44336 0%, #da190b 100%);
}

.notification.warning {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Header et Stats Container */
.header-stats-container {
  background: linear-gradient(135deg, #075e54 0%, #128c7e 100%);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-stats-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent);
  z-index: 0;
}

.chatbot-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.brain-icon {
  color: white;
}

.bot-info {
  flex: 1;
}

.bot-name {
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  color: white;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.bot-description {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
  font-weight: 400;
}

.bot-status {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #dc3545;
  transition: background 0.3s ease;
}

.status-dot.active {
  background: #25d366;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.jarvis-toggle-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.jarvis-toggle {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.jarvis-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.3);
  transition: 0.3s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: #25d366;
}

input:checked + .toggle-slider:before {
  transform: translateX(20px);
}

.toggle-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.menu-button {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.menu-button:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Statistics Bar - Style inspiré de ChatbotHomePage */
.stats-bar {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 1rem 1.5rem 1.5rem;
  border-bottom: none;
  background: transparent;
  box-shadow: none;
  position: relative;
  z-index: 1;
}

/* Nouvelles cartes de statistiques inspirées de ChatbotHomePage */
.stats-card {
  background: linear-gradient(135deg, #128C7E, #075E54);
  border-radius: 16px;
  padding: 20px;
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
}

.stats-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
}

.stats-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.15), transparent 80%);
  z-index: 0;
}

/* Patterns pour chaque carte de statistiques */
.stats-card:nth-child(1) {
  background: linear-gradient(135deg, #128C7E, #075E54);
}

.stats-card:nth-child(1)::after {
  content: '';
  position: absolute;
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
  border-radius: 50%;
  top: -70px;
  right: -70px;
  z-index: 0;
}

.stats-card:nth-child(2) {
  background: linear-gradient(135deg, #34B7F1, #0277BD);
}

.stats-card:nth-child(2)::after {
  content: '';
  position: absolute;
  width: 120px;
  height: 120px;
  border: 15px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  bottom: -80px;
  left: -60px;
  z-index: 0;
}

.stats-card:nth-child(3) {
  background: linear-gradient(135deg, #2ecc71, #27ae60);
}

.stats-card:nth-child(3)::after {
  content: '';
  position: absolute;
  width: 100px;
  height: 200px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transform: rotate(-45deg);
  top: -50px;
  right: -20px;
  z-index: 0;
}

.stats-card:nth-child(4) {
  background: linear-gradient(135deg, #9b59b6, #8e44ad);
}

.stats-card:nth-child(4)::after {
  content: '';
  position: absolute;
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  transform: rotate(45deg);
  top: -40px;
  left: -40px;
  z-index: 0;
}

/* Animation de pulsation pour les cercles icons */
@keyframes pulse {
  from {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

.circle-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  animation: pulse 2s infinite;
}

.stats-card:nth-child(1) .circle-icon {
  background: rgba(255, 255, 255, 0.25);
  animation-delay: 0s;
}

.stats-card:nth-child(2) .circle-icon {
  background: rgba(255, 255, 255, 0.25);
  animation-delay: 0.5s;
}

.stats-card:nth-child(3) .circle-icon {
  background: rgba(255, 255, 255, 0.25);
  animation-delay: 1s;
}

.stats-card:nth-child(4) .circle-icon {
  background: rgba(255, 255, 255, 0.25);
  animation-delay: 1.5s;
}

.circle-icon svg {
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2));
}

.stats-info {
  flex: 1;
  z-index: 1;
}

.stats-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.stats-value {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.stats-value .number {
  font-size: 2.2rem;
  font-weight: 700;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: -0.5px;
}

/* Responsive pour les petits écrans */
@media (max-width: 768px) {
  .stats-bar {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    padding: 0 16px;
  }
  
  .stats-card {
    padding: 16px;
    gap: 12px;
  }
  
  .circle-icon {
    width: 40px;
    height: 40px;
  }
  
  .circle-icon svg {
    width: 20px;
    height: 20px;
  }
  
  .stats-value .number {
    font-size: 1.8rem;
  }
}

@media (max-width: 480px) {
  .stats-bar {
    grid-template-columns: 1fr;
  }
}

/* Main Content */
.knowledge-main {
  background: #f5f5f5;
  padding: 1.5rem;
}

/* Scraping Section - Full Width */
.scraping-section {
  margin-bottom: 3rem;
}

/* Upload and Library Section */
.upload-library-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
}

.upload-card, .scraping-card, .knowledge-library {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

/* Upload Area */
.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 16px;
  padding: 3rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
}

.upload-area:hover, .upload-area.drag-over {
  border-color: #075e54;
  background: rgba(7, 94, 84, 0.05);
  transform: translateY(-2px);
}

.upload-area.uploading {
  border-color: #4CAF50;
  background: rgba(76, 175, 80, 0.05);
  cursor: not-allowed;
}

.upload-content svg {
  color: #075e54;
  margin-bottom: 1rem;
}

.upload-content h3 {
  color: #1f2937;
  margin: 0 0 1rem;
  font-size: 1.3rem;
  font-weight: 600;
}

.upload-content p {
  color: #6b7280;
  margin: 0.5rem 0;
  font-size: 0.95rem;
}

.size-limit {
  font-size: 0.85rem !important;
  color: #9ca3af !important;
}

/* Upload Progress */
.upload-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.progress-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: conic-gradient(#4CAF50 0%, #4CAF50 var(--progress, 0%), #e5e7eb var(--progress, 0%));
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.progress-circle::before {
  content: '';
  position: absolute;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: white;
}

.progress-value {
  position: relative;
  z-index: 1;
  font-weight: 600;
  color: #4CAF50;
}

/* Selected Files */
.selected-files {
  border-top: 1px solid #e5e7eb;
  padding-top: 2rem;
}

.selected-files h4 {
  margin: 0 0 1rem;
  color: #1f2937;
  font-size: 1.1rem;
}

.files-grid {
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
}

.file-preview {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.file-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #075e54;
  color: white;
  border-radius: 8px;
}

.file-info {
  flex: 1;
}

.file-name {
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
}

.file-size {
  color: #6b7280;
  font-size: 0.85rem;
}

.remove-file {
  background: #ef4444;
  border: none;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
}

.remove-file:hover {
  background: #dc2626;
}

/* Upload Actions */
.upload-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.btn-cancel, .btn-upload {
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-cancel {
  background: #e5e7eb;
  color: #6b7280;
}

.btn-cancel:hover {
  background: #d1d5db;
}

.btn-upload {
  background: #075e54;
  color: white;
}

.btn-upload:hover {
  background: #128c7e;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(7, 94, 84, 0.3);
}

.btn-upload:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Scraping Card */
.card-header {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
}

.card-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #075e54;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.card-title h3 {
  margin: 0 0 0.5rem;
  color: #1f2937;
  font-size: 1.2rem;
}

.card-title p {
  margin: 0;
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.4;
  font-weight: 400;
}

/* Scraping Stats - Top Position */
.scraping-stats-top {
  margin-bottom: 25px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.stat-card.available {
  border-left: 4px solid #10b981;
}

.stat-card.selected {
  border-left: 4px solid #3b82f6;
}

.stat-card.processed {
  border-left: 4px solid #8b5cf6;
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  border-radius: 10px;
  background: #f3f4f6;
}

.stat-card.available .stat-icon {
  background: #d1fae5;
  color: #10b981;
}

.stat-card.selected .stat-icon {
  background: #dbeafe;
  color: #3b82f6;
}

.stat-card.processed .stat-icon {
  background: #ede9fe;
  color: #8b5cf6;
}

.stat-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}

.stat-label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.btn-scrape {
  width: 100%;
  padding: 1rem;
  background: #075e54;
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.btn-scrape:hover {
  background: #128c7e;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(18, 140, 126, 0.3);
}

.btn-scrape:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-scrape.scraping {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.scraping-progress {
  background: #f9fafb;
  border-radius: 8px;
  padding: 1rem;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: #075e54;
  transition: width 0.3s ease;
}

.progress-text {
  text-align: center;
  font-size: 0.85rem;
  color: #6b7280;
}

/* Knowledge Library */
.knowledge-library {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.library-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.library-header h2 {
  margin: 0;
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: 700;
}

.library-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-box svg {
  position: absolute;
  left: 12px;
  color: #9ca3af;
  z-index: 1;
}

.search-box input {
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  font-size: 0.9rem;
  width: 250px;
  background: #f9fafb;
  transition: all 0.2s;
}

.search-box input:focus {
  outline: none;
  border-color: #075e54;
  background: white;
  box-shadow: 0 0 0 3px rgba(7, 94, 84, 0.1);
}

.filter-select {
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  background: #f9fafb;
  color: #374151;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-select:focus {
  outline: none;
  border-color: #075e54;
  box-shadow: 0 0 0 3px rgba(7, 94, 84, 0.1);
}

/* Loading and Empty States */
.loading-state, .empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

.empty-state svg {
  color: #d1d5db;
  margin-bottom: 1rem;
}

.empty-state h3 {
  margin: 0 0 0.5rem;
  color: #374151;
}

/* Files Grid Library */
.files-grid-library {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.file-card {
  background: #f9fafb;
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
  cursor: pointer;
}

.file-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #075e54;
}

.file-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.file-type-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.file-type-badge.image {
  background: #dbeafe;
  color: #1e40af;
}

.file-type-badge.pdf {
  background: #fecaca;
  color: #b91c1c;
}

.file-type-badge.document {
  background: #d1fae5;
  color: #059669;
}

.file-type-badge.text {
  background: #fef3c7;
  color: #d97706;
}

.file-type-badge.other {
  background: #e5e7eb;
  color: #6b7280;
}

.file-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.preview-btn {
  background: #3b82f6;
  color: white;
}

.preview-btn:hover {
  background: #2563eb;
}

.delete-btn {
  background: #ef4444;
  color: white;
}

.delete-btn:hover {
  background: #dc2626;
}

.file-content {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.file-icon-large {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #075e54;
  color: white;
  border-radius: 12px;
}

.file-icon-large img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}

.file-info-detailed {
  flex: 1;
  min-width: 0;
}

.file-name-detailed {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  word-break: break-word;
}

.file-metadata {
  display: flex;
  gap: 1rem;
  font-size: 0.85rem;
  color: #6b7280;
}

.file-size-meta, .file-date {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

/* Products Section Styles */
.products-section {
  margin-bottom: 3rem;
}

.products-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.products-stats-top {
  margin-bottom: 25px;
}

.products-filter-section {
  margin-bottom: 2rem;
}

.search-products {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 300px;
}

.search-products svg {
  position: absolute;
  left: 12px;
  color: #9ca3af;
  z-index: 1;
}

.search-products input {
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  font-size: 0.9rem;
  width: 100%;
  background: #f9fafb;
  transition: all 0.2s;
}

.search-products input:focus {
  outline: none;
  border-color: #075e54;
  background: white;
  box-shadow: 0 0 0 3px rgba(7, 94, 84, 0.1);
}

.products-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: white;
}

.product-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
  transition: all 0.2s;
  cursor: pointer;
}

.product-item:last-child {
  border-bottom: none;
}

.product-item:hover {
  background: #f9fafb;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.product-item:hover .product-checkbox .checkbox-label {
  border-color: #075e54;
}

.product-item.selected {
  background: linear-gradient(90deg, rgba(7, 94, 84, 0.08) 0%, rgba(7, 94, 84, 0.02) 100%);
  border-left: 4px solid #075e54;
  box-shadow: 0 2px 8px rgba(7, 94, 84, 0.1);
  transform: translateX(2px);
}

.product-item.selected:hover {
  background: linear-gradient(90deg, rgba(7, 94, 84, 0.12) 0%, rgba(7, 94, 84, 0.04) 100%);
  transform: translateX(2px) translateY(-1px);
  box-shadow: 0 4px 12px rgba(7, 94, 84, 0.15);
}

.product-checkbox {
  flex-shrink: 0;
  margin-top: 0.25rem;
  position: relative;
}

.product-checkbox input[type="checkbox"] {
  display: none;
}

.product-checkbox .checkbox-label {
  width: 22px;
  height: 22px;
  border: 2px solid #d1d5db;
  border-radius: 6px;
  display: block;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.product-checkbox .checkbox-label:hover {
  border-color: #075e54;
  transform: scale(1.05);
  box-shadow: 0 2px 6px rgba(7, 94, 84, 0.2);
}

.product-checkbox .checkbox-label::after {
  content: '';
  position: absolute;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  top: 3px;
  left: 7px;
  opacity: 0;
  transition: all 0.2s ease;
}

.product-checkbox input[type="checkbox"]:checked + .checkbox-label {
  background: linear-gradient(135deg, #075e54 0%, #128c7e 100%);
  border-color: #075e54;
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(7, 94, 84, 0.3);
  animation: checkboxPulse 0.3s ease-in-out;
}

.product-checkbox input[type="checkbox"]:checked + .checkbox-label::after {
  opacity: 1;
  transform: rotate(45deg) scale(1.1);
}

@keyframes checkboxPulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1.1);
  }
}

.product-info {
  flex: 1;
  min-width: 0;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  gap: 1rem;
}

.product-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  word-break: break-word;
}

.product-price {
  padding: 0.25rem 0.75rem;
  background: #10b981;
  color: white;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.product-description {
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;
  word-break: break-word;
}

.product-meta {
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: #9ca3af;
  flex-wrap: wrap;
}

.product-category {
  padding: 0.2rem 0.5rem;
  background: #e5e7eb;
  color: #6b7280;
  border-radius: 12px;
  font-weight: 500;
}

.product-image {
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-products-found, .no-products-available {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
  font-style: italic;
}

.loading-products {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

/* Global Collect Section */
.global-collect-section {
  background: linear-gradient(135deg, #075e54 0%, #128c7e 100%);
  padding: 2rem 1.5rem;
  margin-top: 2rem;
  position: sticky;
  bottom: 0;
  z-index: 50;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
}

.collect-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
}

.collect-info {
  flex: 1;
  color: white;
}

.collect-info h3 {
  margin: 0 0 0.5rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
}

.collect-info p {
  margin: 0 0 1rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
}

.collect-summary {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
}

.summary-item strong {
  color: #25d366;
  font-weight: 700;
  font-size: 1.1rem;
}

.btn-global-collect {
  padding: 1rem 2rem;
  background: #25d366;
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-width: 250px;
  box-shadow: 0 4px 16px rgba(37, 211, 102, 0.3);
}

.btn-global-collect:hover:not(:disabled) {
  background: #20b55a;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(37, 211, 102, 0.4);
}

.btn-global-collect:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 4px 16px rgba(37, 211, 102, 0.2);
}

.btn-global-collect.collecting {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
  box-shadow: 0 4px 16px rgba(255, 152, 0, 0.3);
}

.global-collection-progress {
  margin-top: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.global-collection-progress .progress-bar {
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.global-collection-progress .progress-fill {
  height: 100%;
  background: #25d366;
  transition: width 0.3s ease;
}

.global-collection-progress .progress-text {
  text-align: center;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .upload-library-section {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .library-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .library-actions {
    width: 100%;
    justify-content: space-between;
  }
}

@media (max-width: 768px) {
  .whatsapp-header {
    padding: 0.75rem 1rem;
  }
  
  .header-left {
    gap: 0.75rem;
  }
  
  .bot-avatar {
    width: 36px;
    height: 36px;
  }
  
  .brain-icon {
    width: 24px;
    height: 24px;
  }
  
  .bot-name {
    font-size: 1rem;
  }
  
  .bot-status {
    font-size: 0.8rem;
  }
  
  .stats-bar {
    padding: 0.75rem 1rem;
  }
  
  .stat-number {
    font-size: 1rem;
  }
  
  .stat-label {
    font-size: 0.7rem;
  }
  
  .knowledge-main {
    padding: 1rem;
  }
  
  .upload-card, .scraping-card, .knowledge-library {
    padding: 1rem;
  }
  
  .files-grid-library {
    grid-template-columns: 1fr;
  }
  
  .jarvis-toggle-container {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .toggle-label {
    font-size: 0.75rem;
  }
  
  .search-box input {
    width: 200px;
  }
  
  .library-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
}

@media (max-width: 480px) {
  .upload-area {
    padding: 2rem 1rem;
  }
  
  .upload-content h3 {
    font-size: 1.1rem;
  }
  
  .back-button {
    top: 10px;
    left: 10px;
    padding: 0.5rem 0.75rem;
  }
  
  .search-box input {
    width: 150px;
  }
}

/* Blocks Filter Section */
.blocks-filter-section {
  margin-bottom: 2rem;
}

.filter-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.search-blocks {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 300px;
}

.search-blocks svg {
  position: absolute;
  left: 12px;
  color: #9ca3af;
  z-index: 1;
}

.search-blocks input {
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  font-size: 0.9rem;
  width: 100%;
  background: #f9fafb;
  transition: all 0.2s;
}

.search-blocks input:focus {
  outline: none;
  border-color: #075e54;
  background: white;
  box-shadow: 0 0 0 3px rgba(7, 94, 84, 0.1);
}

.selection-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-select-all, .btn-clear-selection {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-select-all:hover, .btn-clear-selection:hover {
  background: #f3f4f6;
  border-color: #075e54;
}

.btn-clear-selection {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}

.btn-clear-selection:hover {
  background: #fee2e2;
}

/* Blocks List */
.blocks-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: white;
}

.block-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
  transition: all 0.2s;
  cursor: pointer;
}

.block-item:last-child {
  border-bottom: none;
}

.block-item:hover {
  background: #f9fafb;
}

.block-item.selected {
  background: rgba(7, 94, 84, 0.05);
  border-left: 3px solid #075e54;
}

.block-checkbox {
  flex-shrink: 0;
  margin-top: 0.25rem;
}

.block-checkbox input[type="checkbox"] {
  display: none;
}

.checkbox-label {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  display: block;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
}

.checkbox-label::after {
  content: '';
  position: absolute;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  top: 2px;
  left: 6px;
  opacity: 0;
  transition: opacity 0.2s;
}

.block-checkbox input[type="checkbox"]:checked + .checkbox-label {
  background: #075e54;
  border-color: #075e54;
}

.block-checkbox input[type="checkbox"]:checked + .checkbox-label::after {
  opacity: 1;
}

.block-info {
  flex: 1;
  min-width: 0;
}

.block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  gap: 1rem;
}

.block-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  word-break: break-word;
}

.block-type {
  padding: 0.25rem 0.75rem;
  background: #e5e7eb;
  color: #6b7280;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.block-preview {
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;
  word-break: break-word;
}

.block-meta {
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: #9ca3af;
}

.block-id, .block-elements {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.no-blocks-found {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
  font-style: italic;
}

/* Claude Result Section */
.claude-result-section {
  margin-top: 2rem;
  border-top: 2px solid #e5e7eb;
  padding-top: 2rem;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.result-title h4 {
  margin: 0 0 0.5rem;
  color: #059669;
  font-size: 1.2rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.result-title p {
  margin: 0;
  color: #6b7280;
  font-size: 0.9rem;
}

.result-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-copy-prompt, .btn-export-prompt, .btn-toggle-prompt {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-copy-prompt {
  background: #3b82f6;
  color: white;
}

.btn-copy-prompt:hover {
  background: #2563eb;
  transform: translateY(-2px);
}

.btn-export-prompt {
  background: #10b981;
  color: white;
}

.btn-export-prompt:hover {
  background: #059669;
  transform: translateY(-2px);
}

.btn-toggle-prompt {
  background: #6b7280;
  color: white;
}

.btn-toggle-prompt:hover {
  background: #4b5563;
  transform: translateY(-2px);
}

/* Result Stats */
.result-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f0fdf4;
  border-radius: 12px;
  border: 1px solid #bbf7d0;
}

.result-stat {
  text-align: center;
}

.result-stat .stat-number {
  display: block;
  font-size: 1.3rem;
  font-weight: 700;
  color: #059669;
  margin-bottom: 0.25rem;
}

.result-stat .stat-label {
  font-size: 0.8rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}



/* Prompt Display - Editable */
.prompt-display {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #f8fafc;
  margin-bottom: 1.5rem;
}

.prompt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #1e293b;
  border-bottom: 1px solid #334155;
}

.prompt-label-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.prompt-label {
  color: #e2e8f0;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.prompt-changed-indicator {
  color: #fbbf24;
  font-size: 0.8rem;
  font-weight: 600;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.prompt-controls {
  display: flex;
  gap: 0.5rem;
}

.btn-reset-prompt, .btn-format-prompt, .btn-save-prompt {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-reset-prompt {
  background: #ef4444;
  color: white;
}

.btn-reset-prompt:hover {
  background: #dc2626;
}

.btn-format-prompt {
  background: #3b82f6;
  color: white;
}

.btn-format-prompt:hover {
  background: #2563eb;
}

.btn-save-prompt {
  background: #10b981;
  color: white;
}

.btn-save-prompt:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.btn-save-prompt:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.prompt-editor {
  padding: 0;
  background: #1e293b;
}

.prompt-textarea {
  width: 100%;
  min-height: 400px;
  padding: 1.5rem;
  border: none;
  outline: none;
  background: #1e293b;
  color: #e2e8f0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  line-height: 1.6;
  resize: vertical;
  tab-size: 2;
}

.prompt-textarea:disabled {
  background: #334155;
  color: #94a3b8;
  cursor: not-allowed;
}

.prompt-textarea::placeholder {
  color: #64748b;
}

/* Prompt Console Container - Side by Side Layout */
.prompt-console-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 25px;
  margin-top: 20px;
}

@media (max-width: 1200px) {
  .prompt-console-container {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

/* Claude Test Section - Console Style */
.claude-test-section {
  margin-top: 0;
  padding: 0;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.test-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-bottom: 1px solid #475569;
}

.test-title h5 {
  margin: 0;
  color: #e2e8f0;
  font-size: 1.1rem;
  font-weight: 600;
}

.test-title p {
  margin: 0.25rem 0 0;
  color: #94a3b8;
  font-size: 0.85rem;
}

.test-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #94a3b8;
  font-weight: 500;
}

.status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  transition: background 0.3s;
}

.test-status.active .status-indicator {
  background: #10b981;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.4);
}

.test-status.active {
  color: #a7f3d0;
}

.test-console {
  padding: 1.5rem;
  background: #0f172a;
}

.console-input {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  margin-bottom: 0.75rem;
}

.input-wrapper {
  flex: 1;
  display: flex;
  align-items: flex-start;
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 8px;
  padding: 0.75rem;
  transition: all 0.2s;
}

.input-wrapper:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.console-prompt {
  color: #10b981;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-weight: 600;
  font-size: 0.9rem;
  margin-right: 0.75rem;
  margin-top: 0.1rem;
  flex-shrink: 0;
}

.console-textarea {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: #e2e8f0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  resize: none;
  min-height: 20px;
  max-height: 120px;
}

.console-textarea:disabled {
  color: #64748b;
  cursor: not-allowed;
}

.console-textarea::placeholder {
  color: #64748b;
}

.btn-console-send {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-console-send:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-console-send:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
}

.console-hint {
  text-align: center;
  padding: 0.5rem;
  border-top: 1px solid #1e293b;
}

.console-hint span {
  color: #64748b;
  font-size: 0.8rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

/* Test Results */
.test-results {
  border-top: 1px solid #e2e8f0;
  padding-top: 1.5rem;
}

.test-results h6 {
  margin: 0 0 1rem;
  color: #1e293b;
  font-size: 1rem;
  font-weight: 600;
}

.test-result-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.test-result-item {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.test-message {
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f1f5f9;
}

.test-message strong {
  color: #1e293b;
}

.test-response {
  margin-bottom: 0.75rem;
}

.test-response strong {
  color: #1e293b;
}

.test-response.success {
  color: #059669;
}

.test-response.error {
  color: #dc2626;
}

.response-json {
  margin-top: 0.5rem;
  padding: 1rem;
  background: #1e293b;
  border-radius: 8px;
  overflow-x: auto;
}

.response-success {
  margin-top: 0.75rem;
}

.response-summary {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
}

.response-item {
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.response-item:last-child {
  margin-bottom: 0;
}

.response-item strong {
  color: #059669;
  min-width: 100px;
}

.redirection-block {
  background: #10b981;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.85rem;
}

.response-details {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.response-details summary {
  background: #f9fafb;
  padding: 0.75rem 1rem;
  cursor: pointer;
  font-weight: 500;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  transition: background 0.2s;
}

.response-details summary:hover {
  background: #f3f4f6;
}

.response-details[open] summary {
  background: #1e293b;
  color: #e2e8f0;
}

.response-json {
  margin-top: 0.5rem;
  padding: 1rem;
  background: #1e293b;
  border-radius: 0 0 8px 8px;
  overflow-x: auto;
}

.response-json code {
  color: #e2e8f0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  white-space: pre;
}

.response-error {
  margin-top: 0.5rem;
  padding: 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 0.9rem;
}

.test-timestamp {
  font-size: 0.8rem;
  color: #94a3b8;
  text-align: right;
}

.btn-clear-tests {
  padding: 0.5rem 1rem;
  background: #6b7280;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1rem;
}

.btn-clear-tests:hover {
  background: #4b5563;
}

/* Scrollbar for prompt text */
.prompt-content::-webkit-scrollbar {
  width: 8px;
}

.prompt-content::-webkit-scrollbar-track {
  background: #334155;
}

.prompt-content::-webkit-scrollbar-thumb {
  background: #64748b;
  border-radius: 4px;
}

.prompt-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Blocks list scrollbar */
.blocks-list::-webkit-scrollbar {
  width: 8px;
}

.blocks-list::-webkit-scrollbar-track {
  background: #f3f4f6;
}

.blocks-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

.blocks-list::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

@media (max-width: 768px) {
  .whatsapp-header {
    padding: 0.75rem 1rem;
  }
  
  .header-left {
    gap: 0.75rem;
  }
  
  .bot-avatar {
    width: 36px;
    height: 36px;
  }
  
  .brain-icon {
    width: 24px;
    height: 24px;
  }
  
  .bot-name {
    font-size: 1rem;
  }
  
  .bot-status {
    font-size: 0.8rem;
  }
  
  .stats-bar {
    padding: 0.75rem 1rem;
  }
  
  .stat-number {
    font-size: 1rem;
  }
  
  .stat-label {
    font-size: 0.7rem;
  }
  
  .knowledge-main {
    padding: 1rem;
  }
  
  .upload-card, .scraping-card, .knowledge-library {
    padding: 1rem;
  }
  
  .files-grid-library {
    grid-template-columns: 1fr;
  }
  
  .jarvis-toggle-container {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .toggle-label {
    font-size: 0.75rem;
  }
  
  .search-box input {
    width: 200px;
  }
  
  .library-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  /* Mobile adaptations for products */
  .collect-container {
    flex-direction: column;
    text-align: center;
    gap: 1.5rem;
  }
  
  .collect-summary {
    justify-content: center;
  }
  
  .btn-global-collect {
    min-width: 100%;
  }
  
  .product-item {
    flex-direction: column;
    gap: 1rem;
  }
  
  .product-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .product-meta {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .product-image {
    align-self: center;
    width: 120px;
    height: 120px;
  }
  
  /* Mobile adaptations for blocks */
  .filter-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-blocks {
    max-width: none;
  }
  
  .selection-actions {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .block-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .block-meta {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .prompt-stats {
    flex-direction: column;
    gap: 1rem;
  }
  
  .result-actions {
    margin-left: 0;
    margin-top: 1rem;
  }
  
  .result-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .result-stats {
    flex-direction: column;
    gap: 1rem;
  }
  
  .console-input {
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }
  
  .test-console {
    padding: 1rem;
  }
  
  .test-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .prompt-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
}

@media (max-width: 480px) {
  .upload-area {
    padding: 2rem 1rem;
  }
  
  .upload-content h3 {
    font-size: 1.1rem;
  }
  
  .back-button {
    top: 10px;
    left: 10px;
    padding: 0.5rem 0.75rem;
  }
  
  .search-box input {
    width: 150px;
  }
  
  .blocks-list {
    max-height: 300px;
  }
  
  .prompt-content {
    max-height: 400px;
  }
  
  .prompt-text {
    font-size: 0.8rem;
    padding: 1rem;
  }
  
  .test-console {
    padding: 0.75rem;
  }
  
  .console-textarea {
    font-size: 0.85rem;
  }
  
  .btn-console-send {
    width: 40px;
    height: 40px;
  }
  
  .prompt-textarea {
    min-height: 300px;
    font-size: 0.8rem;
    padding: 1rem;
  }
}
</style> 